import { GridApiCommon, GridCellModes, GridRowId, GridRowModes, GridRowModesModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

import { IHotelClinicPassengerType } from "@/models/hotel-clinic.model";
import { HotelClinicListItem } from "@/pages/HotelPlanning/clinic-arrival/types";

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** API identity for create-task — stored on `changes` so header/mutations need no table row snapshot. */
export type ClinicUnassignedPassengerContext =
    | { type: IHotelClinicPassengerType.CLINIC; pickUp: string; operationId: string }
    | { type: IHotelClinicPassengerType.CUSTOM; passengerDbId: string };

function clinicUnassignedMetaFromRow(
    row: HotelClinicListItem
): Pick<ClinicUnassignedEditableFields, "ctx" | "_isPlaceholder" | "name"> {
    if (row._isPlaceholder) {
        return { _isPlaceholder: true, name: row.name, ctx: undefined };
    }
    if (row.type === IHotelClinicPassengerType.CLINIC) {
        return {
            _isPlaceholder: false,
            name: row.name,
            ctx: {
                type: IHotelClinicPassengerType.CLINIC,
                pickUp: row.pickUp,
                operationId: row.id,
            },
        };
    }
    return {
        _isPlaceholder: false,
        name: row.name,
        ctx: {
            type: IHotelClinicPassengerType.CUSTOM,
            passengerDbId: row.id,
        },
    };
}

export type ClinicUnassignedEditableFields = {
    /** Pending driver `userId`; never persist empty/null — cleared rows are removed from `changes`. */
    userId?: string;
    /** Filled from the row on each `processRowUpdate` for this id (API + header without mirroring all rows in the store). */
    ctx?: ClinicUnassignedPassengerContext;
    _isPlaceholder?: boolean;
    /** `row.name` at last edit (confirmation dialog / labels). */
    name?: string;
};

export const clinicUnassignedKeysToCancel: (keyof ClinicUnassignedEditableFields)[] = ["userId"];

type ClinicUnassignedTableState = {
    /** API + page date for hotel clinic arrival (replaces a separate `use-hotel-clinic-arrival-date` store). */
    clinicArrivalDate: DateTime;
    /**
     * When true, rows with `cancelled` are excluded (same semantics as Hotel Planning arrival `filters.cancelled`).
     */
    cancelled: boolean;
    rowModesModel: GridRowModesModel;
    isFullScreen: boolean;
    changes: Record<GridRowId, ClinicUnassignedEditableFields>;
    initialValues: Record<GridRowId, ClinicUnassignedEditableFields>;
};

type ClinicUnassignedTableActions = {
    actions: {
        handleRowModesModelChange: (rowModesModel: GridRowModesModel) => void;
        updateFullScreen: (val?: boolean) => void;
        getChangedKeys: (
            newRow: HotelClinicListItem,
            oldRow: HotelClinicListItem
        ) => [ClinicUnassignedEditableFields, number];
        processRowUpdate: (newRow: HotelClinicListItem, oldRow: HotelClinicListItem) => HotelClinicListItem;
        handleRowModesModelCancel: (
            rowId: GridRowId,
            apiRef: GridApiCommon,
            setInitial?: boolean,
            fieldKey?: keyof ClinicUnassignedEditableFields
        ) => void;
        resetRowChanges: (ids?: GridRowId[]) => void;
        resetRowInitials: (ids?: GridRowId[]) => void;
        getCellInitialValue: <T>(id: GridRowId, field: keyof ClinicUnassignedEditableFields) => T | false;
        setClinicArrivalDate: (date: DateTime) => void;
        toggleCancelledPassengersFilter: () => void;
    };
};

function rowKey(row: HotelClinicListItem): GridRowId {
    return row.uniqueIdentifier;
}

function sanitizeClinicUnassignedChanges(
    ch: Record<GridRowId, ClinicUnassignedEditableFields>
): Record<GridRowId, ClinicUnassignedEditableFields> {
    const out: Record<GridRowId, ClinicUnassignedEditableFields> = {};
    for (const [id, fields] of Object.entries(ch)) {
        if (!fields || typeof fields !== "object") continue;
        const u = fields.userId != null && String(fields.userId).trim() !== "" ? String(fields.userId).trim() : null;
        if (!u) continue;
        out[id] = { ...fields, userId: u };
    }
    return out;
}

export const useClinicUnassignedPassengersTableStore = create<
    ClinicUnassignedTableState & ClinicUnassignedTableActions
>()((set, get) => ({
    clinicArrivalDate: DateTime.now().startOf("day"),
    cancelled: false,
    rowModesModel: {},
    isFullScreen: false,
    changes: {},
    initialValues: {},
    actions: {
        setClinicArrivalDate: (date) => set({ clinicArrivalDate: date.startOf("day") }),
        toggleCancelledPassengersFilter: () => set((s) => ({ cancelled: !s.cancelled })),
        updateFullScreen: (isFullScreen) => set({ isFullScreen: isFullScreen ?? !get().isFullScreen }),
        handleRowModesModelChange: (rowModesModel) => set({ rowModesModel }),
        resetRowChanges: (ids?: GridRowId[]) => {
            if (!ids) {
                set({ changes: {} });
                return;
            }
            const next = { ...get().changes };
            for (const id of ids) delete next[id];
            set({ changes: next });
        },
        resetRowInitials: (ids?: GridRowId[]) => {
            if (!ids) {
                set({ initialValues: {} });
                return;
            }
            const next = { ...get().initialValues };
            for (const id of ids) delete next[id];
            set({ initialValues: next });
        },
        getCellInitialValue: <T>(id: GridRowId, field: keyof ClinicUnassignedEditableFields) =>
            get().initialValues[id] && (get().initialValues[id][field] as T),
        getChangedKeys: (newRow: HotelClinicListItem, oldRow: HotelClinicListItem) => {
            const changes: ClinicUnassignedEditableFields = {};
            const oldU = oldRow.userId != null ? String(oldRow.userId).trim() : "";
            const newU = newRow.userId != null ? String(newRow.userId).trim() : "";
            if (newU !== oldU) {
                if (newU) {
                    changes.userId = newU;
                }
            }
            return [changes, Object.keys(changes).length] as [ClinicUnassignedEditableFields, number];
        },
        processRowUpdate: (newRow: HotelClinicListItem, oldRow: HotelClinicListItem) => {
            const id = rowKey(newRow);
            const newUid =
                newRow.userId != null && String(newRow.userId).trim() !== "" ? String(newRow.userId).trim() : null;
            const pending = get().changes[id];

            if (!newUid && pending && pending.userId != null) {
                const next = { ...get().changes };
                delete next[id];
                set({ changes: sanitizeClinicUnassignedChanges(next) });
                return newRow;
            }

            const [changedFields, changesAmount] = get().actions.getChangedKeys(newRow, oldRow);
            if (!changesAmount) return oldRow;

            const newChanges = { ...get().changes };

            try {
                for (const key of Object.keys(changedFields) as (keyof ClinicUnassignedEditableFields)[]) {
                    const initialData = get().initialValues[id];
                    const initialValue =
                        initialData && get().initialValues[id][key as keyof ClinicUnassignedEditableFields];
                    const changedVal = changedFields[key];

                    if (changedVal === initialValue) {
                        delete changedFields[key];
                        if (newChanges[id] && !Object.keys(newChanges[id]).length) delete newChanges[id];
                    }

                    if (!initialData || !(key in initialData)) {
                        const newVal = { ...get().initialValues };
                        newVal[id] = {
                            ...(newVal[id] || {}),
                            [key]: oldRow[key as keyof HotelClinicListItem] as string & null,
                        };
                        set({ initialValues: newVal });
                    }
                }
            } catch (err) {
                console.error(err);
            }

            if (newChanges[id] || Object.keys(changedFields).length) {
                const fromRow = clinicUnassignedMetaFromRow(newRow);
                newChanges[id] = { ...(newChanges[id] || {}), ...fromRow, ...changedFields };
            }
            set({ changes: sanitizeClinicUnassignedChanges(newChanges) });

            return newRow;
        },
        handleRowModesModelCancel: async (uniqueIdentifier, apiRef, setInitial = true, fieldKey) => {
            try {
                if (!apiRef) return;
                for (const field of clinicUnassignedKeysToCancel) {
                    if (fieldKey && fieldKey !== field) continue;
                    const initialData = get().initialValues[uniqueIdentifier];
                    const initialValue = initialData && get().initialValues[uniqueIdentifier][field];
                    const cell = { id: uniqueIdentifier, field };
                    const cellMode = apiRef.getCellMode(uniqueIdentifier, field);

                    if (!setInitial || !initialData || !(field in initialData)) {
                        if (cellMode === GridCellModes.Edit) apiRef.stopCellEditMode(cell);
                        continue;
                    }

                    if (cellMode !== GridCellModes.Edit) {
                        await apiRef.startCellEditMode(cell);
                    }
                    await wait(25);
                    await apiRef.setEditCellValue({
                        id: uniqueIdentifier,
                        field,
                        value: initialValue ?? null,
                    });
                    await wait(25);
                    apiRef.stopCellEditMode(cell);
                }
            } catch (err) {
                console.error("#clinicUnassigned handleRowModesModelCancel", err);
            }

            await wait(60);
            get().actions.resetRowChanges([uniqueIdentifier]);
            get().actions.resetRowInitials([uniqueIdentifier]);
            get().actions.handleRowModesModelChange({
                ...get().rowModesModel,
                [uniqueIdentifier]: { mode: GridRowModes.View, ignoreModifications: true },
            });
        },
    },
}));

export const useClinicUnassignedChanges = () => useClinicUnassignedPassengersTableStore((s) => s.changes);
export const useClinicUnassignedRowModesModel = () => useClinicUnassignedPassengersTableStore((s) => s.rowModesModel);
export const useClinicUnassignedIsFullScreen = () => useClinicUnassignedPassengersTableStore((s) => s.isFullScreen);
export const useClinicUnassignedRowChanges = (id: GridRowId) =>
    useClinicUnassignedPassengersTableStore((s) => s.changes[id]);
export const useClinicUnassignedIsChanged = (id: GridRowId, field: keyof ClinicUnassignedEditableFields) =>
    useClinicUnassignedPassengersTableStore((s) => s.changes[id] && field in s.changes[id]);
export const useClinicUnassignedInitialVal = (id: GridRowId, field: keyof ClinicUnassignedEditableFields) =>
    useClinicUnassignedPassengersTableStore((s) => s.initialValues[id] && s.initialValues[id][field]);
export const useClinicUnassignedPassengersTableActions = () =>
    useClinicUnassignedPassengersTableStore((s) => s.actions);

/** Same flag meaning as Hotel Planning toolbar `filters.cancelled` — sent on unassigned export. */
export const useClinicUnassignedCancelledFilter = () => useClinicUnassignedPassengersTableStore((s) => s.cancelled);

/** Clinic arrival page / API date (shared slice on this store; same `setDate` key as the old `use-hotel-clinic-arrival-date` API). */
export const useHotelClinicArrivalDate = () => useClinicUnassignedPassengersTableStore((s) => s.clinicArrivalDate);
/** Use `useShallow` — a plain object selector would be a new ref every time and Zustand would re-subscribe in a loop. */
export const useHotelClinicArrivalDateActions = () =>
    useClinicUnassignedPassengersTableStore(useShallow((s) => ({ setDate: s.actions.setClinicArrivalDate })));
